import { Node } from '@tiptap/core';
import BubbleMenuExtension from '@tiptap/extension-bubble-menu';
import { CodeBlockLowlight } from '@tiptap/extension-code-block-lowlight';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Strike from '@tiptap/extension-strike';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import { Transaction } from '@tiptap/pm/state';
import { EditorContent, useEditor, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { all, createLowlight } from 'lowlight';
import React from 'react';
import { Markdown } from 'tiptap-markdown';

import { BubbleMenuBar } from './bubble-menu';
import { MenuBar } from './menu-bar';

import '@/styles/highlight-gh-light.css';
import '@/styles/markdown-editor.css';
import { Details, DetailsSummary, DetailsContent } from '@/lib/tiptap-extension-details';
import { ImageUpload } from '@/lib/tiptap-extension-image-upload';
import { RichTextLink } from '@/lib/tiptap-extension-md-link';
import { createCollaboratorMentionExtension } from '@/lib/tiptap-extension-mention';
import { VideoUpload } from '@/lib/tiptap-extension-video-upload';
import { cn } from '@/lib/utils';
import type { GithubCollaborator } from '@/services/repo';

const lowlight = createLowlight(all);

// Custom MesaDescription node for mesa-description conversion. This is needed to ensure tiptap/prosemirror do not strip the tags we use to detect mesa-generated descriptions.
const MesaDescription = Node.create({
  name: 'mesaDescription',
  inline: true,
  group: 'inline',
  atom: true,
  selectable: false,

  addAttributes() {
    return {
      variant: {
        default: 'start',
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-mesa-description]',
        getAttrs: (dom) => {
          if (typeof dom === 'string') return {};
          const el = dom as HTMLElement;
          return { variant: el.getAttribute('data-mesa-description') };
        },
      },
    ];
  },

  renderHTML({ node }) {
    return ['span', { 'data-mesa-description': node.attrs.variant }];
  },
});

export interface MarkdownEditorProps {
  value: string;
  onChange?: (value: string) => void;
  onBlur?: ({ editor, event, transaction }: { editor: Editor; event: FocusEvent; transaction: Transaction }) => void;
  onFocus?: () => void;
  placeholder?: string;
  className?: string;
  showStaticMenu?: boolean;
  showBubbleMenu?: boolean;
  autofocus?: boolean;
  editable?: boolean;
  mentionCollaborators?: GithubCollaborator[];
  owner: string;
  repo?: string;
}

export const MarkdownEditor = React.forwardRef<Editor, MarkdownEditorProps>(
  (
    {
      value,
      onChange,
      onBlur,
      onFocus,
      placeholder = 'Write something...',
      className,
      showStaticMenu = false,
      showBubbleMenu = false,
      editable = true,
      autofocus = false,
      mentionCollaborators,
      owner,
      repo,
    },
    ref
  ) => {
    const collaboratorsRef = React.useRef<GithubCollaborator[]>(mentionCollaborators ?? []);

    React.useEffect(() => {
      collaboratorsRef.current = mentionCollaborators ?? [];
    }, [mentionCollaborators]);

    // Create the extension only once. It reads collaborators from ref each time.
    const mentionExt = React.useMemo(() => createCollaboratorMentionExtension(() => collaboratorsRef.current), []);

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          codeBlock: false, // We use CodeBlockLowlight instead
          strike: false, // We use the standalone Strike extension
          horizontalRule: false, // We use the standalone HorizontalRule extension
        }),
        Markdown.configure({
          linkify: true,
          transformPastedText: true,
          transformCopiedText: true,
          breaks: true,
          html: true,
          tightLists: false,
          bulletListMarker: '-',
        }),
        BubbleMenuExtension,
        Placeholder.configure({
          placeholder,
          emptyEditorClass: 'is-editor-empty',
        }),
        RichTextLink,
        Strike,
        Subscript,
        Superscript,
        HorizontalRule,
        Table.configure({
          resizable: true,
        }),
        TableRow,
        TableHeader,
        TableCell,
        CodeBlockLowlight.configure({
          lowlight,
        }),
        TaskList.configure({
          itemTypeName: 'taskItem',
        }),
        TaskItem.configure({
          nested: true,
        }),
        Details,
        DetailsSummary,
        DetailsContent,
        MesaDescription,
        Image.configure({
          HTMLAttributes: {
            class: 'mesa-image',
            style: 'max-width: 100%; max-height: 500px; height: auto; border-radius: 4px;',
          },
        }),
        repo ? ImageUpload.configure({ owner, repo }) : null,
        repo ? VideoUpload.configure({ owner, repo }) : null,
        ...(mentionExt ? [mentionExt] : []),
      ],
      editable: editable,
      onFocus: () => {
        onFocus?.();
      },
      autofocus,
      content: value,
      onUpdate: ({ editor }) => {
        const markdown = editor.storage.markdown.getMarkdown();
        onChange?.(markdown);
      },
      onBlur,
      editorProps: {
        attributes: {
          class: cn('markdown-editor', 'focus:outline-none', 'min-h-0'),
        },
      },
    });

    // Keep editor content in sync with external `value` prop updates. Useful for read-only
    // instances where the parent swaps out the markdown (e.g., changing `replyQuote`).
    React.useEffect(() => {
      if (!editor) return;

      const currentMarkdown = editor.storage.markdown.getMarkdown();
      if (value !== currentMarkdown) {
        // `emitUpdate: false` avoids triggering `onUpdate` and causing a loop.
        editor.commands.setContent(value, false);
      }
    }, [value, editor]);

    React.useImperativeHandle(ref, () => editor!, [editor]);

    if (!editor) {
      return null;
    }

    return (
      <div>
        {/* Menu bars */}
        {showStaticMenu && <MenuBar editor={editor} />}
        {showBubbleMenu && <BubbleMenuBar editor={editor} />}

        {/* Editor content */}
        <EditorContent editor={editor} className={className} />
      </div>
    );
  }
);
